import { AloftDate, MILES_TO_METERS, SubjectData } from '@aloft/toolkit-shared';

import {
  LocationType,
  getLocationTypeProximityRequirementMiles,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { getPropertyTypeFilterValue } from '../internal/FilterPanel/propertyTypeOptions.ts';
import { InitialCompSearch } from '../types.ts';

export const DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE = 'Single Family Residence';

const GLA_PERCENTAGE_BY_LOCATION_TYPE: Record<LocationType, number> = {
  Urban: 10,
  Suburban: 20,
  Rural: 40,
};

const SITE_SIZE_PERCENTAGE_BY_LOCATION_TYPE: Record<LocationType, number> = {
  Urban: 25,
  Suburban: 50,
  Rural: 100,
};

export const getDefaultGlaPercentage = (locationType: LocationType | undefined): number =>
  GLA_PERCENTAGE_BY_LOCATION_TYPE[locationType ?? 'Suburban'];

export const getDefaultSiteSizePercentage = (locationType: LocationType | undefined): number =>
  SITE_SIZE_PERCENTAGE_BY_LOCATION_TYPE[locationType ?? 'Suburban'];

export const getDefaultRecommendedCompSearch = (
  subject: SubjectData,
  _effectiveDate: AloftDate,
): InitialCompSearch['body']['search'] => {
  const { locationType, coordinates } = subject.editableData.propertyIdentification;
  const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
  const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType) ?? 12;
  const propertySubType = getPropertyTypeFilterValue(
    subject.editableData.improvements.propertyType,
  );

  return {
    filters: {
      status: '',
      propertySubType: propertySubType
        ? [propertySubType]
        : [DEFAULT_RECOMMENDED_COMP_PROPERTY_SUB_TYPE],
      saleDate: {
        variance: saleDateRequirementMonths,
      },
      salePrice: {},
      gla: {
        percentage: getDefaultGlaPercentage(locationType),
      },
      lotSize: {
        percentage: getDefaultSiteSizePercentage(locationType),
      },
      yearBuilt: {},
      bedrooms: {},
      fullBaths: {},
      halfBaths: {},
      stories: {
        variance: '',
      },
      garageSpaces: {},
      quality: {},
      condition: {},
    },
    geoSearch: {
      t: 'radius',
      radiusInMeters: (proximityRequirementMiles ?? 1) * MILES_TO_METERS,
      coord: [coordinates?.longitude ?? 0, coordinates?.latitude ?? 0],
    },
    matchCriteria: {
      saleDate: 'lasting_range',
      salePrice: 'min_max_range',
      gla: 'percentage_range',
      lotSize: 'percentage_range',
      yearBuilt: 'min_max_range',
      bedrooms: 'min_max_range',
      fullBaths: 'min_max_range',
      halfBaths: 'min_max_range',
      stories: 'value_range',
      garageSpaces: 'min_max_range',
      quality: 'min_max_range',
      condition: 'min_max_range',
    },
  } as unknown as InitialCompSearch['body']['search'];
};
